import { isApple, appleProducts, restoreApple } from '../applePurchases';
import Dialog from './Dialog';
import React, { useEffect, useRef, useState } from 'react';
import { PRO_INFO_URL } from '../constants';
import { isNative } from '../native';

const FEATURE_COPY = {
  routines: {
    title: 'Guided routines are part of Pro',
    body: 'Five guided routines — A Moment of Calm, Before Sleep, Feeling Overwhelmed, Wake Up, and Refocus and Reset — plus Kids Mode’s friendly animals and the full colour palette (Free Tap always keeps blue and purple).',
  },
  kids: {
    title: 'Kids Mode is part of Pro',
    body: 'Friendly animals and gentle sounds, designed for a child to use with an adult.',
  },
  colours: {
    title: 'More colours are part of Pro',
    body: 'Pro opens the full calming colour collection. Free Tap always includes TapTap blue and purple.',
  },
  ended: {
    title: 'This Pro session has ended',
    body: 'Your access changed while TapTap was open, so we returned you safely to Free Tap.',
  },
  overview: {
    title: 'What’s in TapTap Pro',
    body: 'Five guided routines, Kids Mode with friendly animals and gentle sounds, and more calming colours. Routines live on Home; Kids Mode and colours are in Settings.',
    informationOnly: true,
  },
};

const ProUpsell = ({ feature = 'routines', onClose, onContinueFree, onUpgrade, onStartTrial, onResendVerification, hasProAccess = false }) => {
  const [products, setProducts] = useState({});
  const [pricesFailed, setPricesFailed] = useState(false);
  const [priceAttempt, setPriceAttempt] = useState(0);
  useEffect(() => {
    if (!isApple) return undefined;
    let cancelled = false;
    setPricesFailed(false);
    appleProducts().then(value => { if (!cancelled) { setProducts(value); setPricesFailed(!value.monthly || !value.annual); } })
      .catch(() => { if (!cancelled) setPricesFailed(true); });
    return () => { cancelled = true; };
  }, [priceAttempt]);
  const firstActionRef = useRef(null);
  const [status, setStatus] = useState({ state: 'idle', message: '' }); // idle | error | trial-used | unverified | resent
  // Which single action is actually in flight - kept separate from `status`
  // so clicking one button never shows a *different* button's busy label
  // (e.g. the trial button used to say "Starting trial…" while a Stripe
  // checkout was loading, because both shared one generic 'working' flag).
  const [busy, setBusy] = useState(null); // null | 'trial' | 'monthly' | 'annual' | 'resend'

  // Stripe checkout navigates the whole page away and back on cancel. Some
  // browsers restore that from cache (bfcache) exactly as it was mid-request
  // - including `busy` still set - so every button looks permanently stuck
  // "opening" even though nothing is actually running. Clear it on restore.
  useEffect(() => {
    const onPageShow = (event) => { if (event.persisted) setBusy(null); };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);
  const [showPlans, setShowPlans] = useState(false);
  const copy = FEATURE_COPY[showPlans ? 'routines' : feature] || FEATURE_COPY.routines;
  const showUpgrade = !!onUpgrade && !copy.informationOnly && (!isNative || isApple);
  const nativePurchaseBlocked = isNative && !isApple && !!onUpgrade && !copy.informationOnly;
  const appleTrial = isApple && products.monthly?.eligibleSevenDayTrial === true;
  const showTrial = !isApple && !!onStartTrial && !copy.informationOnly && status.state !== 'trial-used';

  const handleStartTrial = async () => {
    setBusy('trial');
    setStatus({ state: 'idle', message: '' });
    try {
      await onStartTrial();
      // Pro is now live via the entitlement listener — close so the user
      // sees the unlocked screen behind the modal, not a static success state.
      onClose?.();
    } catch (error) {
      if (error?.code === 'functions/already-exists') {
        setStatus({ state: 'trial-used', message: isNative && !isApple ? 'This email has already used its free trial. You can continue with Free Tap.' : 'This email has already used its free trial — you can still subscribe below.' });
      } else if (error?.code === 'functions/failed-precondition' && error?.message?.includes('verify your email')) {
        setStatus({ state: 'unverified', message: 'Check your inbox (and spam folder) for a verification link, then try again.' });
      } else {
        setStatus({ state: 'error', message: 'Could not start your trial. Please try again.' });
      }
    } finally {
      setBusy(null);
    }
  };

  const handleResendVerification = async () => {
    setBusy('resend');
    try {
      await onResendVerification?.();
      setStatus({ state: 'resent', message: 'Verification email sent — check your inbox (and spam folder), then try again.' });
    } catch {
      setStatus({ state: 'unverified', message: 'Could not resend the email. Please try again shortly.' });
    } finally {
      setBusy(null);
    }
  };

  const handleUpgrade = async (plan) => {
    setBusy(plan);
    setStatus({ state: 'idle', message: '' });
    try {
      const result = await onUpgrade(plan);
      if (isApple) {
        if (result?.status === 'purchased') onClose?.();
        else if (result?.status === 'pending') setStatus({ state: 'pending', message: 'Your purchase is awaiting Apple approval. Pro will unlock when it is approved.' });
        setBusy(null);
      }
    } catch (error) {
      setStatus({ state: 'error', message: error?.code === 'functions/already-exists' ? 'You already have a subscription or a completed checkout. Open Settings to manage billing.' : (isApple ? (error?.message || 'Could not complete your purchase. Please try again.') : 'Could not start checkout. Please try again shortly.') });
      setBusy(null);
    }
  };


  return (
    <Dialog
      onClose={onClose}
      initialFocusRef={firstActionRef}
      labelledBy="pro-title"
      describedBy="pro-description"
      dismissOnBackdrop
      className="fixed inset-0 z-dialog flex items-center justify-center overflow-y-auto bg-black/85 p-5 backdrop-blur-sm"

    >
      <div
        className="relative my-auto max-h-full w-full max-w-sm overflow-y-auto rounded-[2rem] border border-indigo-300/20 bg-gradient-to-b from-slate-900 to-[#10172a] p-6 text-white shadow-2xl landscape:max-w-lg landscape:p-4"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close TapTap Pro information"
          className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.07] text-xl text-white/65 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
        >
          <span aria-hidden="true">×</span>
        </button>

        <div className="flex items-center gap-2" aria-hidden="true">
          <span className="h-8 w-8 rounded-full bg-blue-500 shadow-[0_0_20px_rgba(59,130,246,.65)]" />
          <span className="h-8 w-8 rounded-full bg-violet-500 shadow-[0_0_20px_rgba(139,92,246,.65)]" />
        </div>
        <span className="mt-6 inline-flex rounded-full bg-violet-500/15 px-3 py-1 text-[0.625rem] font-bold uppercase tracking-[0.18em] text-violet-200 landscape:mt-3">
          TapTap Pro
        </span>
        <h2 id="pro-title" className="mt-3 pr-10 text-2xl font-extrabold landscape:mt-2 landscape:text-xl">{copy.title}</h2>
        <p id="pro-description" className="mt-3 text-sm leading-relaxed text-white/65 landscape:mt-2">{copy.body}</p>
        <p className="mt-3 text-sm leading-relaxed text-indigo-200/80 landscape:mt-2">Free Tap remains available whenever you need it.</p>

        <div className="mt-6 grid gap-3 landscape:mt-3 landscape:gap-2">
          {copy.informationOnly && !hasProAccess && (onStartTrial || ((!isNative || isApple) && onUpgrade)) && (
            <button ref={firstActionRef} type="button" onClick={() => setShowPlans(true)} className="min-h-11 rounded-xl bg-white/10 px-5 py-3 text-sm font-bold text-white">
              {isNative && !isApple ? 'Explore the free trial' : 'See trial and subscription options'}
            </button>
          )}
          {showTrial && (
            <button
              ref={firstActionRef}
              type="button"
              disabled={busy !== null}
              onClick={handleStartTrial}
              className="min-h-11 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-violet-500/20 active:scale-[.99] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            >
              {busy === 'trial' ? 'Starting trial…' : 'Start 7-day free trial'}
            </button>
          )}
          {showTrial && (
            <p className="text-center text-[0.6875rem] text-white/65">
              No card needed. Reverts to Free automatically — nothing is ever charged unless you choose to subscribe.
            </p>
          )}

          {(status.state === 'unverified' || status.state === 'resent') && onResendVerification && (
            <button
              type="button"
              disabled={busy !== null || status.state !== 'unverified'}
              onClick={handleResendVerification}
              className="min-h-11 rounded-xl border border-white/15 bg-white/[0.06] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/[0.1] active:scale-[.99] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
            >
              {busy === 'resend' ? 'Resending…' : 'Resend verification email'}
            </button>
          )}

          {showUpgrade ? (
            <div className={`grid gap-3 ${appleTrial ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <button
                ref={showTrial ? undefined : firstActionRef}
                type="button"
                disabled={busy !== null || (isApple && !products.monthly)}
                onClick={() => handleUpgrade('monthly')}
                className="min-h-11 rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-white/[0.1] active:scale-[.99] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
              >
                {busy === 'monthly' ? (isApple ? 'Opening Apple…' : 'Opening checkout…') : isApple ? (products.monthly ? (appleTrial ? `Start 7-day free trial — then ${products.monthly.displayPrice}/month` : `${products.monthly.displayPrice}/month`) : (pricesFailed ? 'Monthly — unavailable' : 'Monthly — loading…')) : '£2.99/month'}
              </button>
              <button
                type="button"
                disabled={busy !== null || (isApple && !products.annual)}
                onClick={() => handleUpgrade('annual')}
                className="min-h-11 rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-white/[0.1] active:scale-[.99] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
              >
                {busy === 'annual' ? (isApple ? 'Opening Apple…' : 'Opening checkout…') : isApple ? (products.annual ? `${products.annual.displayPrice}/year` : (pricesFailed ? 'Annual — unavailable' : 'Annual — loading…')) : '£29.99/year'}
              </button>
            </div>
          ) : nativePurchaseBlocked ? (
            <p className="text-center text-xs font-semibold text-white/65">
              Purchases are not available in this app.
            </p>
          ) : !copy.informationOnly ? (
            <p className="text-center text-xs font-semibold text-white/55">
              TapTap Pro is nearly ready
            </p>
          ) : null}
          {isApple && !copy.informationOnly && <>
            {pricesFailed && <p role="status" className="text-center text-xs text-white/65">App Store prices are unavailable. <button type="button" disabled={busy !== null} onClick={() => setPriceAttempt(value => value + 1)} className="underline">Try again</button></p>}
            <p className="text-center text-xs text-white/65">TapTap Pro subscriptions renew automatically until cancelled in your Apple subscription settings. {appleTrial ? `Monthly plan: 7 days free, then ${products.monthly.displayPrice}/month. Cancel at least 24 hours before the trial ends to avoid being charged. The annual plan is charged when you confirm.` : 'Payment is charged to your Apple Account when you confirm.'}</p>
            <button type="button" disabled={busy !== null} className="min-h-11 rounded-xl bg-white/10 text-sm" onClick={async () => {
              setBusy('restore');
              try { const result = await restoreApple(); setStatus({ state: 'restored', message: result.active ? 'Your Pro subscription has been restored.' : 'No active Apple subscription was found.' }); }
              catch (error) { setStatus({ state: 'error', message: error.message || 'Could not restore purchases.' }); }
              finally { setBusy(null); }
            }}>{busy === 'restore' ? 'Restoring…' : 'Restore Purchases'}</button>
            <p className="text-center text-xs text-white/65"><a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener noreferrer" className="underline">Terms of Use</a> · <a href="https://taptap.org.uk/privacy/" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a></p>
          </>}
          {!isNative && !showUpgrade && !showTrial && !hasProAccess && !onUpgrade && !onStartTrial && (
            <a
              ref={firstActionRef}
              href={PRO_INFO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="min-h-11 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-5 py-3 text-center text-sm font-bold text-white shadow-lg shadow-violet-500/20 active:scale-[.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            >
              See TapTap Pro pricing
            </a>
          )}

          {status.message && (
            <p className={`text-center text-xs font-semibold ${status.state === 'error' ? 'text-rose-300' : 'text-white/60'}`} role="status">
              {status.message}
            </p>
          )}

          {onContinueFree && !hasProAccess ? (
            <button
              type="button"
              onClick={onContinueFree}
              className="min-h-11 rounded-xl bg-white/10 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/15 active:scale-[.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
            >
              Continue with Free Tap
            </button>
          ) : (
            <button
              type="button"
              ref={hasProAccess ? firstActionRef : undefined}
              onClick={onClose}
              className="min-h-11 rounded-xl bg-white/10 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/15 active:scale-[.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default ProUpsell;
